import React from 'react';
import PropTypes from 'prop-types';
import Input from '../Input';
import TypeaheadTemplate from './TypeaheadTemplate';
import FakeEvent from '../utils/fake-event';
import { isUndefined } from '../utils/helpers';

function isSection(suggestion) {
  return typeof suggestion !== 'string' && 'suggestions' in suggestion;
}

const getSuggestionValue = (suggestion) => {
  return typeof suggestion === 'string' ? suggestion : suggestion.value;
};

function isMatch(props, inputValue, option) {
  let suggestionVal = isSection(option) ? option.title : getSuggestionValue(option);
  if (props.ignoreCase) {
    suggestionVal = suggestionVal.toLowerCase();
  }
  switch (props.matchPos) {
    case 'any':
      return suggestionVal.includes(inputValue);
    case 'start':
      return suggestionVal.startsWith(inputValue);
    default:
      // Should not be reachable
      return false;
  }
}

const defaultFilterOptions = (value, props) => {
  let inputValue = value.trim();
  if (props.ignoreCase) {
    inputValue = inputValue.toLowerCase();
  }
  const inputLength = inputValue.length;

  if (inputLength === 0) {
    return props.alwaysRenderSuggestions ? props.options : [];
  } else {
    return props.options.reduce((reduction, option) => {
      if (isMatch(props, inputValue, option)) {
        reduction.push(option);
      } else if (isSection(option)) {
        const matchingSuggestions = option.suggestions.filter(isMatch.bind(isMatch, props, inputValue));
        if (matchingSuggestions.length) {
          reduction.push({
            title: option.title,
            suggestions: matchingSuggestions,
          });
        }
      }
      return reduction;
    }, []);
  }
};

class Typeahead extends React.Component {
  constructor(props) {
    super(props);

    const value = isUndefined(props.value) ? (isUndefined(props.defaultValue) ? '' : props.defaultValue) : props.value;
    const suggestions = isUndefined(props.suggestions) ? props.filterOptions(value, props) : props.suggestions;
    const announcement = isUndefined(props.suggestionsAnnouncement) ? null : props.suggestionsAnnouncement;

    this.state = {
      value: value,
      shownSuggestions: suggestions,
      suggestionsAnnouncement: announcement,
    };

    this.handleSuggestionsFetchRequested = this.handleSuggestionsFetchRequested.bind(this);
    this.handleSuggestionsClearRequested = this.handleSuggestionsClearRequested.bind(this);
    this.inputOnChange = this.inputOnChange.bind(this);
    this.shouldRenderSuggestions = this.shouldRenderSuggestions.bind(this);
    this.getSuggestionAnnouncement = this.getSuggestionAnnouncement.bind(this);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const nextState = {};
    let nextValue = prevState.value;

    if (!isUndefined(nextProps.value)) {
      nextValue = nextProps.value;
      nextState.value = nextValue;
    }

    if (nextProps.suggestions) {
      nextState.shownSuggestions = nextProps.suggestions;
    }
    return nextState;
  }

  getSuggestionAnnouncement(numSuggestions) {
    const suggestionSingularOrPlural = numSuggestions === 1 ? 'suggestion' : 'suggestions';

    return `${numSuggestions} ${suggestionSingularOrPlural} available.`;
  }

  handleSuggestionsFetchRequested({ value, reason }) {
    if (this.props.onSuggestionsFetchRequested) {
      this.props.onSuggestionsFetchRequested({ value, reason });
    }
    // set state if this is an uncontrolled component
    if (this.props.options) {
      const shownSuggestions = this.props.filterOptions(value, this.props);
      const announcement = this.getSuggestionAnnouncement(shownSuggestions.length);
      this.setState({
        shownSuggestions: shownSuggestions,
        suggestionsAnnouncement: announcement,
      });
    }
  }

  handleSuggestionsClearRequested() {
    if (this.props.onSuggestionsClearRequested) {
      this.props.onSuggestionsClearRequested();
    }
    // set state if this is an uncontrolled component
    if (this.props.options && !this.props.alwaysRenderSuggestions) {
      this.setState({
        shownSuggestions: [],
        suggestionsAnnouncement: null,
      });
    }
  }

  inputOnChange(event, { newValue, method }) {
    this.setState({
      value: newValue,
    });
    if (this.props.onChange) {
      this.props.onChange(
        event.type === 'change'
          ? { ...event, id: this.props.id }
          : new FakeEvent({ value: newValue, id: this.props.id }),
        {
          newValue,
          method,
        }
      );
    }
  }

  shouldRenderSuggestions(value) {
    if (this.props.shouldRenderSuggestions) {
      return this.props.shouldRenderSuggestions(value);
    } else {
      return value.trim().length >= this.props.minQueryLength;
    }
  }

  render() {
    const {
      className,
      options, // eslint-disable-line no-unused-vars
      filterOptions, // eslint-disable-line no-unused-vars
      minQueryLength, // eslint-disable-line no-unused-vars
      suggestions,
      onSuggestionsFetchRequested, // eslint-disable-line no-unused-vars
      onSuggestionsClearRequested, // eslint-disable-line no-unused-vars
      onChange, // eslint-disable-line no-unused-vars
      getSuggestionText,
      onSuggestionSelected,
      alwaysRenderSuggestions,
      matchPos, // eslint-disable-line no-unused-vars
      ignoreCase, // eslint-disable-line no-unused-vars
      placeholder,
      disabled,
      error,
      icon,
      iconSet,
      // react-autosuggest API props that will be passed to Autosuggest
      onSuggestionHighlighted, // eslint-disable-line react/prop-types
      highlightFirstSuggestion, // eslint-disable-line react/prop-types
      shouldRenderSuggestions, // eslint-disable-line no-unused-vars
      focusInputOnSuggestionClick,
      forwardedRef,
      theme, // eslint-disable-line react/prop-types
      id, // eslint-disable-line react/prop-types
      multiSection,
      renderSectionTitle,
      getSectionSuggestions,
      renderSuggestion,
      /* eslint-disable no-unused-vars, react/prop-types */
      getSuggestionValue: getSuggestionValueProp,
      // props we don't want to pass to input
      defaultValue,
      /* eslint-enable no-unused-vars */
      ...rest
    } = this.props;

    const { value, shownSuggestions, suggestionsAnnouncement } = this.state;

    const inputProps = {
      placeholder,
      value,
      onChange: this.inputOnChange,
      disabled,
      error,
      icon,
      iconSet,
      ref: forwardedRef,
      ...rest,
    };

    return (
      <TypeaheadTemplate
        suggestions={shownSuggestions}
        onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
        getSuggestionText={getSuggestionText}
        getSuggestionValue={getSuggestionValue}
        inputProps={inputProps}
        renderInputComponent={(props) => <Input {...props} />}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={this.shouldRenderSuggestions}
        alwaysRenderSuggestions={isUndefined(alwaysRenderSuggestions) ? !!suggestions : alwaysRenderSuggestions}
        className={className}
        suggestionsAnnouncement={suggestionsAnnouncement}
        multiSection={multiSection}
        renderSectionTitle={renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
        renderSuggestion={renderSuggestion}
        // other props from react-autosuggest that aren't used or explicitly called out in our API
        onSuggestionHighlighted={onSuggestionHighlighted}
        highlightFirstSuggestion={highlightFirstSuggestion}
        focusInputOnSuggestionClick={focusInputOnSuggestionClick}
        theme={theme}
        id={id}
      />
    );
  }
}

const suggestionsValidator = (props, propName, componentName) => {
  if (isUndefined(props[propName])) {
    return null;
  }
  if (!Array.isArray(props[propName])) {
    return new Error(`Invalid prop ${propName} supplied to ${componentName}. Must be an array.`);
  }
  for (const [index, suggestion] of props[propName].entries()) {
    if (typeof suggestion === 'object') {
      if (suggestion.suggestions) {
        const error = suggestionsValidator({ suggestions: suggestion.suggestions }, 'suggestions', componentName);
        if (error) {
          return error;
        }
      } else if (!suggestion.value) {
        return new Error(
          `Invalid prop ${propName}[${index}] supplied to ${componentName}. Object must include \`value\` key`
        );
      }
    } else if (typeof suggestion != 'string') {
      return new Error(`Invalid prop ${propName}[${index}] supplied to ${componentName}. Must be an object or string.`);
    }
  }
};

Typeahead.propTypes = {
  /** When this is `true`, `suggestions` or `options` provided to the component will always be displayed - this defaults
   * to `false` when `options` are provided and `true` when `suggestions` are provided but can be overridden */
  alwaysRenderSuggestions: PropTypes.bool,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Determines the initial value of the typeahead, but allows it to be updated ([uncontrolled](https://reactjs.org/docs/uncontrolled-components.html)).
   *  Will be used to set the state if a value prop is not defined. */
  defaultValue: PropTypes.string,
  /** Adds the relevant Forge state class to the input and passes prop to native react `<input />` */
  disabled: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Entirely replaces `options` filtering logic. Receives the following params:
   * * **value** (String): The input value
   * * **props** (Object): The props passed into your decorated component.
   */
  filterOptions: PropTypes.func,
  /** When true, the input remains focused when suggestions are clicked/tapped. */
  focusInputOnSuggestionClick: PropTypes.bool,
  /** Replaces default function for determining what text gets exposed to the screen reader for available suggestions - should return a string. Used to generate the `suggestionsAnnouncement` state.
   * Receives the following params:
   * * **numSuggestions** (Number): Number of suggestions currently available
   */
  getSuggestionAnnouncement: PropTypes.func,
  /** Replaces default function for determining what text gets displayed for a suggestion - should return a string
   * Receives the following params:
   * * **suggestion** (Object): One of the user defined objects passed to the options property
   */
  getSuggestionText: PropTypes.func,
  /** Hides any required styling
   * Can be set manually or by the required field variation set for `Form`.
   */
  hideRequiredStyles: PropTypes.bool,
  /** Optionally display an icon in the left side of the Input. Provide an small
   * icon component from the @athena/forge-icons package.
   *
   * Deprecated usage since 4.0.0: Providing a string to use the `Icon` component.
   */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** Deprecated since version 4.0.0
   *
   * Specify a custom icon set defined as a dictionary of `IconBase` components */
  iconSet: PropTypes.objectOf(PropTypes.func),
  /** ID assigned to the input */
  id: PropTypes.string,
  /** Whether to perform case-insensitive filtering `options` */
  ignoreCase: PropTypes.bool,
  /** Match the start or entire string when filtering `options`  */
  matchPos: PropTypes.oneOf(['any', 'start']),
  /** For use with `options` prop, this defines how many characters need to be typed by the user before showing
   * suggestions */
  minQueryLength: PropTypes.number,
  /** Function that takes a JS event as an argument. Called when the Typeahead loses focus */
  onBlur: PropTypes.func,
  /** Called when the text inside the input changes — passes the event and the value of the input */
  onChange: PropTypes.func,
  /** Function that takes a JS event as an argument. Called when the Typeahead receives focus */
  onFocus: PropTypes.func,
  /** Will be called every time you need to set `suggestions` to `[]`. (if not using `options`) */
  onSuggestionsClearRequested: PropTypes.func,
  /** Will be called every time you need to recalculate `suggestions`. (if not using `options`) */
  onSuggestionsFetchRequested: PropTypes.func,
  /** Called when a suggestion is selected - this function gets passed directly to the `Autosuggest` plugin  */
  onSuggestionSelected: PropTypes.func,
  /** Array of string or object suggestion options that will automatically be filtered and provided to the `Autosuggest`
   * plugin - when using objects.
   * By default, `value` will be displayed in the suggestion dropdown
   */
  options: suggestionsValidator,
  /** Text shown before typing in the text input */
  placeholder: PropTypes.string,
  /** Ref forwarded to the input element */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    /** Note: { current: PropTypes.instanceOf(HTMLInputElement) } would be more
     * more correct, but causes a StackOverflow when building gatsby.
     */
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  /** Indicates if it is a required field. Passed as an attribute to the native react <input /> */
  required: PropTypes.bool,
  /** Function called to render suggestions. Is passed the value. */
  shouldRenderSuggestions: PropTypes.func,
  /** Array of string or object suggestions that will get passed to the `Autosuggest` plugin — by default, these will be
   * rendered immediately unless `alwaysRenderSuggestions` is set to `false` */
  suggestions: suggestionsValidator,
  /** String read out by screen reader (using aria-live region) to notify users that suggestions are being displayed.
   *  Setting this to null will clear the aria-live region.
   *  Only provide this prop if using Typeahead as a [controlled](https://reactjs.org/docs/forms.html#controlled-components) component.
   * */
  suggestionsAnnouncement: PropTypes.string,
  /** Value of the input. If provided, used to set the state. Creates a [controlled](https://reactjs.org/docs/forms.html#controlled-components) component. */
  value: PropTypes.string,
  /** Custom suggestion renderer */
  renderSuggestion: PropTypes.func,
  /** Whether or not this is a multi-section typeahead */
  multiSection: PropTypes.bool,
  /** Custom section title renderer */
  renderSectionTitle: PropTypes.func,
  /** Function for extracting suggestions from a section */
  getSectionSuggestions: PropTypes.func,
  /** Passthrough props to [React Autosuggest](https://github.com/moroshko/react-autosuggest#inputPropsProp)'s inputProps */
  '...rest': PropTypes.any,
};

Typeahead.defaultProps = {
  getSuggestionText: getSuggestionValue,
  minQueryLength: 1,
  matchPos: 'any',
  ignoreCase: true,
  filterOptions: defaultFilterOptions,
  disabled: false,
  required: false,
  hideRequiredStyles: false,
};

function forwardRef(props, ref) {
  return <Typeahead {...props} forwardedRef={ref} />;
}

forwardRef.displayName = 'Typeahead';

export default React.forwardRef(forwardRef);
